/**
 * Блок «Медиамониторинг» (FE-9, redesign по макету): счётчики тональности
 * (позитивных / нейтральных / негативных — по всем публикациям) + top-3
 * публикаций с цветным акцентом тональности слева.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS) → мок
 * hardData/mediaMonitoring.json (счётчики + публикации с «источник ·
 * N дней назад»); остальные КАТО → /api/public/media-monitoring.
 *
 * Каркас блока виден всегда: без данных счётчики показывают названия без
 * значений, а вместо публикаций — три строки-заготовки с цветными краями.
 *
 * - Фетчит `/api/public/media-monitoring?kato=` (BE-8) при изменении `kato`.
 * - Сортировка top-3 по `like_sum desc`, `slice(0, 3)` — на стороне FE
 *   (§5.2 ТЗ говорит «BE отдаёт полный массив»).
 * - Тональность (значения бэка): 2 — позитив, 1 — нейтраль, -2 — негатив.
 *   Цветной край слева от публикации + sr-only текст (a11y);
 *   неизвестный/`null` tone — без края.
 * - Кликабельность: если `url` есть — заголовок-ссылка с
 *   `target="_blank" rel="noopener noreferrer"`; если нет — просто текст.
 * - Под текстом — статистика публикации (§5.2): лайки, комментарии,
 *   репосты («самолётик»); «аудитория» убрана (замечание дизайн-ревью).
 */
import { Heart, MessageCircle, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BlockLoader } from '../../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { isHardDataKato } from '../../../../hardData/constants';
import mediaHard from '../../../../hardData/mediaMonitoring.json';
import { cn } from '../../../../lib/cn';
import { getMediaMonitoring } from '../../../../requests/public.request';
import type { MediaPublication } from '../../../../types/public';

interface Props {
  kato: number;
  /**
   * Уведомляет родителя о загруженных публикациях (null — запрос упал):
   * по ним можно считать производные показатели (например, кликабельные).
   * Через ref внутри — идентичность колбэка не влияет на перефетч.
   */
  onPublications?: (publications: MediaPublication[] | null) => void;
}

// accentClass — цветной левый край плашки публикации (толстый border-left,
// изгибается вместе со скруглением углов — как на макете).
// tone — значения бэка: 2 позитив / 1 нейтраль / -2 негатив.
const TONES = [
  {
    key: 'positive',
    tone: 2,
    valueClass: 'text-brand-600',
    accentClass: 'border-l-chart-green',
  },
  {
    key: 'neutral',
    tone: 1,
    valueClass: 'text-text-muted',
    accentClass: 'border-l-surface-border',
  },
  {
    key: 'negative',
    tone: -2,
    valueClass: 'text-status-danger',
    accentClass: 'border-l-status-danger',
  },
] as const;

const top3ByLikes = (pubs: MediaPublication[]): MediaPublication[] =>
  [...pubs].sort((a, b) => b.like_sum - a.like_sum).slice(0, 3);

export const MediaMonitoringBlock = ({ kato, onPublications }: Props) => {
  const { t } = useTranslation();
  const [publications, setPublications] = useState<MediaPublication[] | null>(
    null,
  );

  // Демо-режим: счётчики и публикации из hardData, без запроса к API.
  const isHardKato = isHardDataKato(kato);

  const onPublicationsRef = useRef(onPublications);
  onPublicationsRef.current = onPublications;

  useEffect(() => {
    if (isHardKato) return;
    let cancelled = false;
    getMediaMonitoring(kato)
      .then((response) => {
        if (!cancelled) {
          setPublications(response.publications);
          onPublicationsRef.current?.(response.publications);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublications([]);
          onPublicationsRef.current?.(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [kato, isHardKato]);

  const top = publications ? top3ByLikes(publications) : [];

  return (
    <Card className="h-full shadow-card">
      <CardHeader>
        <CardTitle>{t('digitalPassport.mediaMonitoring.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <section data-testid="block-media-monitoring">
          {/* Счётчики тональности видны всегда: без данных — название и
              пустое значение (та же логика, что у карточек сайдбара). */}
          <div className="grid grid-cols-3 gap-3">
            {TONES.map(({ key, tone, valueClass }) => (
              <div
                key={key}
                data-testid={`media-tone-${key}`}
                className="rounded-xl border border-surface-border bg-gradient-to-b from-surface to-surface-page px-4 py-3 shadow-card"
              >
                <p
                  className={cn(
                    'min-h-8 text-2xl font-extrabold tabular-nums',
                    valueClass,
                  )}
                  data-testid={`media-tone-${key}-value`}
                >
                  {isHardKato
                    ? mediaHard.counters[key]
                    : publications !== null &&
                      publications.filter((pub) => pub.tone === tone).length}
                </p>
                <p className="mt-1.5 text-xs text-text-muted">
                  {t(`digitalPassport.mediaMonitoring.counters.${key}`)}
                </p>
              </div>
            ))}
          </div>

          {isHardKato ? (
            <ul data-testid="media-list" className="mt-3.5 flex flex-col gap-3">
              {mediaHard.publications.map((pub) => {
                const toneMeta = TONES.find((item) => item.tone === pub.tone);
                return (
                  <li
                    key={pub.text}
                    data-testid="media-card"
                    className={cn(
                      'min-w-0 rounded-2xl border-l-[6px] bg-surface-muted/60 px-5 py-4',
                      toneMeta ? toneMeta.accentClass : 'border-l-transparent',
                    )}
                  >
                    <span className="block text-[17px] font-semibold leading-snug">
                      {pub.text}
                    </span>
                    <p className="mt-1.5 text-[13px] text-text-muted">
                      {pub.source} ·{' '}
                      {t('digitalPassport.mediaMonitoring.daysAgo', {
                        count: pub.daysAgo,
                      })}
                    </p>
                  </li>
                );
              })}
            </ul>
          ) : publications === null ? (
            // Публикации ещё грузятся с бэка.
            <BlockLoader testId="media-loader" />
          ) : top.length > 0 ? (
            <ul data-testid="media-list" className="mt-3.5 flex flex-col gap-3">
              {top.map((pub, idx) => (
                <PublicationCard
                  key={`${idx}-${pub.text.slice(0, 32)}`}
                  pub={pub}
                />
              ))}
            </ul>
          ) : (
            // Нет публикаций (загрузка / пусто / ошибка) — три пустые
            // строки-заготовки с цветными краями тональности (зелёный /
            // красный / серый, как на макете), чтобы блок держал форму.
            <ul
              data-testid="media-list-empty"
              className="mt-3.5 flex flex-col gap-3"
            >
              {(['positive', 'negative', 'neutral'] as const).map((key) => (
                <li
                  key={key}
                  data-testid="media-placeholder"
                  className={cn(
                    'min-h-[4.5rem] rounded-2xl border-l-[6px] bg-surface-muted/60 px-5 py-4',
                    TONES.find((item) => item.key === key)?.accentClass,
                  )}
                />
              ))}
            </ul>
          )}
        </section>
      </CardContent>
    </Card>
  );
};

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

const PublicationCard = ({ pub }: { pub: MediaPublication }) => {
  const { t } = useTranslation();
  const toneMeta = TONES.find((item) => item.tone === pub.tone);

  // Статистика публикации: лайки / комментарии / репосты («самолётик»);
  // «аудитория» убрана (замечание дизайн-ревью).
  const stats = [
    { key: 'likes', icon: Heart, value: pub.like_sum },
    { key: 'comments', icon: MessageCircle, value: pub.comment_sum },
    { key: 'shares', icon: Send, value: pub.shared_sum },
  ].flatMap((stat) =>
    stat.value === null ? [] : [{ ...stat, value: stat.value }],
  );

  return (
    <li data-testid="media-card" className="flex">
      <div
        {...(toneMeta && {
          'data-testid': 'media-accent',
          'data-tone': pub.tone,
        })}
        className={cn(
          'min-w-0 flex-1 rounded-2xl border-l-[6px] bg-surface-muted/60 px-5 py-4',
          toneMeta ? toneMeta.accentClass : 'border-l-transparent',
        )}
      >
        {toneMeta && (
          <span className="sr-only">
            {pub.tone_text ||
              t(`digitalPassport.mediaMonitoring.tone.${toneMeta.key}`)}
          </span>
        )}
        {pub.url ? (
          <a
            href={pub.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="media-link"
            className="line-clamp-2 text-[14px] font-semibold leading-snug hover:underline"
          >
            {pub.text}
          </a>
        ) : (
          <span
            data-testid="media-text"
            className="line-clamp-2 text-[17px] font-semibold leading-snug"
          >
            {pub.text}
          </span>
        )}
        <ul
          data-testid="media-stats"
          className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-text-muted"
        >
          {stats.map(({ key, icon: Icon, value }) => (
            <li
              key={key}
              data-testid={`media-stat-${key}`}
              className="flex items-center gap-1.5"
              title={t(`digitalPassport.mediaMonitoring.stats.${key}`)}
            >
              <Icon className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="sr-only">
                {t(`digitalPassport.mediaMonitoring.stats.${key}`)}:{' '}
              </span>
              <span className="font-semibold tabular-nums">{fmt(value)}</span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};
